import useSWR from 'swr';

import {
  TheraAdvanceResponse,
  TheraAdvanceHistoryResponse,
  TheraAdvanceTransactionDetailResponse,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/advance/resources/theraAdvance';
import { TheraAdvanceAccountStatusResponse } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bank/resources/bankApi';
import { globalFetcherSettings, TheraAdvanceTransactionType } from 'lib/constants';
import { useTheraFernClient } from './useFern';

export const useTheraAdvanceSWR = ({ theraAdvanceId }: { theraAdvanceId?: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const fetcher = async (): Promise<TheraAdvanceResponse> => {
    return theraFernApi.thera.advance.theraAdvance.getTheraAdvance(theraAdvanceId);
  };

  const { data, error, mutate, isLoading } = useSWR(
    theraAdvanceId ? ['getTheraAdvance', theraAdvanceId] : false,
    fetcher,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  const theraAdvance: TheraAdvanceResponse | null = data || null;

  return {
    theraAdvance,
    mutateAdvance: mutate,
    isPending: isLoading,
    error,
  };
};

export const useTheraAdvanceHistorySWR = ({ theraAdvanceId }: { theraAdvanceId?: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const fetcher = async (): Promise<TheraAdvanceHistoryResponse> => {
    return theraFernApi.thera.advance.theraAdvance.getTheraAdvanceHistory(theraAdvanceId);
  };

  const { data, error, mutate, isLoading } = useSWR(
    theraAdvanceId ? ['getTheraAdvanceHistory', theraAdvanceId] : false,
    fetcher,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  return {
    history: data || null,
    entries: data?.entries || [],
    mutateHistory: mutate,
    isPending: isLoading,
    error,
  };
};

export const useTheraAdvanceTransactionDetailSWR = ({
  theraAdvanceId,
  transactionId,
  type,
}: {
  theraAdvanceId?: string;
  transactionId?: string;
  type?: TheraAdvanceTransactionType;
}) => {
  const { theraFernApi } = useTheraFernClient();

  const fetcher = async (): Promise<TheraAdvanceTransactionDetailResponse> => {
    return theraFernApi.thera.advance.theraAdvance.getTheraAdvanceTransactionDetail(theraAdvanceId, transactionId, {
      type,
    });
  };

  const { data, error, isLoading } = useSWR(
    theraAdvanceId && transactionId && type
      ? ['getTheraAdvanceTransactionDetail', theraAdvanceId, transactionId, type]
      : false,
    fetcher,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  return {
    transaction: data || null,
    isPending: isLoading,
    error,
  };
};

export const useTheraAdvanceAccountStatusSWR = ({ userId, companyId }: { userId?: string; companyId?: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const fetcher = async (): Promise<TheraAdvanceAccountStatusResponse> => {
    return theraFernApi.thera.bank.bankApi.getTheraAdvanceAccountStatus(userId, companyId);
  };

  const { data, error, isLoading } = useSWR(
    userId && companyId ? ['getTheraAdvanceAccountStatus', userId, companyId] : false,
    fetcher,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  return {
    accountStatus: data || null,
    isPending: isLoading,
    error,
  };
};
